// Inline-edit label. The label reads as a single clickable field: hover
// shows only a highlight (the click-to-edit affordance), and clicking
// swaps in a focused <input>. The edit surface is where actions live —
// alongside the input we append whatever buttons the caller wired
// (delete, and — for the roadmap — deprecate / restore). There is no
// hover-revealed pencil/add cluster and no hover text-lengthening; the
// caller chooses which actions a given label exposes on click.

import { useEffect, useRef, useState } from 'react';

import { TruncatedLabel } from '../../components/TruncatedLabel.tsx';

import { HoverAction } from './HoverAction.tsx';

export function EditableLabel({
  text,
  isOverlay,
  isDeprecated,
  truncMax,
  placeholder,
  autoEdit,
  onSave,
  onDelete,
  onDeprecate,
  onRestore,
  linkedPlansCount,
  linkedNoun,
}: {
  text: string;
  isOverlay?: boolean;
  /** Open straight into edit mode on mount — for freshly-created rows
   *  (new derivedContext / function / skill) so the user can name them inline
   *  without a second click. The initial text is selected, so typing
   *  replaces the default name. */
  autoEdit?: boolean;
  /** Shown (muted) in view mode when `text` is empty — for optional
   *  fields like a business-function description. Also the input's
   *  placeholder while editing. */
  placeholder?: string;
  /** When true the label text gets a strikethrough and the deprecate
   *  action swaps for restore. */
  isDeprecated?: boolean;
  /** @deprecated Truncation is CSS width-based; retained for call-site
   *  compatibility and ignored. */
  truncMax?: number;
  /** When omitted the label renders as plain, non-editable text. */
  onSave?: (next: string) => void;
  /** When provided, a delete button appears in the edit surface. */
  onDelete?: () => void;
  /** When provided AND `!isDeprecated`, a deprecate button appears in the
   *  edit surface (roadmap projects / teams). */
  onDeprecate?: () => void;
  /** When provided AND `isDeprecated`, a restore button appears instead. */
  onRestore?: () => void;
  /** When > 0, render a persistent "linked" badge next to the label so
   *  the user sees that a rename here syncs to other plans referencing
   *  the same id. */
  linkedPlansCount?: number;
  /** Singular noun used in the tooltip — "derived_context" or "skill". */
  linkedNoun?: 'derived_context' | 'skill';
}) {
  void truncMax; // accepted for back-compat; truncation is CSS-driven now
  const [editing, setEditing] = useState(Boolean(autoEdit));
  const [draft, setDraft] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setDraft(text);
  }, [text]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  // When the label exposes actions but no rename, edit mode shows the
  // action buttons without an input — there's nothing to blur, so close
  // on an outside click instead.
  useEffect(() => {
    if (!editing || onSave) return;
    function onDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setEditing(false);
      }
    }
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [editing, onSave]);

  const linkedBadge =
    linkedPlansCount && linkedPlansCount > 0 ? (
      <LinkedBadge count={linkedPlansCount} noun={linkedNoun ?? 'derived_context'} />
    ) : null;

  // The label is interactive if it can rename OR exposes any action.
  const interactive = Boolean(onSave || onDelete || onDeprecate || onRestore);

  // Non-interactive: plain truncated label (+ optional linked badge). The
  // hover reveal is off so read-only labels match the editor's quiet
  // hover treatment.
  if (!interactive) {
    if (!linkedBadge) {
      return <TruncatedLabel text={text} isNew={isOverlay} noReveal />;
    }
    return (
      <span className='snap-editable snap-editable--static'>
        <TruncatedLabel text={text} isNew={isOverlay} noReveal />
        {linkedBadge}
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    setEditing(false);
    if (onSave && trimmed && trimmed !== text) {
      onSave(trimmed);
    } else {
      setDraft(text);
    }
  };

  if (editing) {
    const hasActions = Boolean(
      onDelete || (onDeprecate && !isDeprecated) || (onRestore && isDeprecated),
    );
    return (
      <span ref={containerRef} className='snap-editable snap-editable--editing'>
        {onSave ? (
          <input
            ref={inputRef}
            className='snap-editable__input'
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            placeholder={placeholder}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditing(false);
                setDraft(text);
              }
            }}
            autoFocus
          />
        ) : (
          <span className='snap-editable__static-text'>{text}</span>
        )}
        {hasActions ? (
          <span className='snap-edit-actions'>
            {!isDeprecated && onDeprecate ? (
              <HoverAction
                kind='deprecate'
                label='Deprecate'
                onClick={() => {
                  setEditing(false);
                  onDeprecate();
                }}
              />
            ) : null}
            {isDeprecated && onRestore ? (
              <HoverAction
                kind='restore'
                label='Restore'
                onClick={() => {
                  setEditing(false);
                  onRestore();
                }}
              />
            ) : null}
            {onDelete ? (
              <HoverAction
                kind='delete'
                label='Delete'
                onClick={() => {
                  setEditing(false);
                  onDelete();
                }}
              />
            ) : null}
          </span>
        ) : null}
      </span>
    );
  }

  const wrapperClasses = ['snap-editable', 'snap-editable--clickable'];
  if (isDeprecated) wrapperClasses.push('snap-editable--deprecated');

  return (
    <span
      className={wrapperClasses.join(' ')}
      role='button'
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setEditing(true);
        }
      }}
    >
      {text || !placeholder ? (
        <TruncatedLabel text={text} isNew={isOverlay} noReveal />
      ) : (
        <span className='snap-editable__placeholder'>{placeholder}</span>
      )}
      {linkedBadge}
    </span>
  );
}

/**
 * Small "linked" affordance rendered next to a label whose id also
 * appears in other plans in the active roadmap. Persistent (not hover-
 * gated) because the warning applies to every action on this row.
 */
function LinkedBadge({
  count,
  noun,
}: {
  count: number;
  noun: 'derived_context' | 'skill';
}) {
  const tooltip =
    `Linked ${noun} — also lives in ${count} other ${count === 1 ? 'plan' : 'plans'} ` +
    'in this roadmap. Renames + new skills sync across all of them on save.';
  return (
    <span
      className='snap-linked-badge'
      data-tooltip={tooltip}
      aria-label={tooltip}
    >
      <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.7'
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden
      >
        <path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72' />
        <path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72' />
      </svg>
    </span>
  );
}
